import { yupResolver } from '@hookform/resolvers/yup';
import {
  useFlowId,
  useForm,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import Decimal from 'decimal.js';
import { useEffect } from 'react';

import {
  PremiumAmountFormFields,
  premiumAmountSchema,
} from './PremiumAmountUtils';

type Props =
  | {
      mode: 'fetch';
      employeeId: string;
      onboardingPeriodId?: string;
      benefitId?: string;
      shouldShowLoadingOverlay?: boolean;
    }
  | {
      mode: 'data';
      employeeMonthlyContribution: number;
      premium: number;
      employerMonthlyContribution: number;
      shouldShowLoadingOverlay?: boolean;
    };

export const usePremiumAmountForm = (props: Props) => {
  const idempotencyKey = useIdempotencyKey();
  const flowId = useFlowId();

  const { shouldShowLoadingOverlay = true } = props;
  const { mutate: mutateBenefitPremium } = useMonolithMutation({
    method: 'benefitsControllerUpdateBenefitPremium',
    shouldReloadPage: false,
    shouldShowLoadingOverlay,
  });

  const shouldFetch = props.mode === 'fetch';
  const onboardingPeriodId = shouldFetch ? props.onboardingPeriodId : undefined;
  const employeeId = shouldFetch ? props.employeeId : undefined;
  const benefitId = shouldFetch ? props.benefitId : undefined;

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: onboardingPeriodId &&
      employeeId && [employeeId, onboardingPeriodId],
    enabled: shouldFetch,
  });
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: onboardingPeriodId && [onboardingPeriodId],
    enabled: shouldFetch,
  });

  const selectedBenefit = benefitId
    ? benefits?.allBenefits.find((benefit) => benefit.id === benefitId)
    : benefits?.majorMedicalBenefit;
  const selectedPlan = props.mode === 'data' ? props : selectedBenefit?.plan;

  const premiumAmountForm = useForm<PremiumAmountFormFields>({
    mode: 'all',
    resolver: yupResolver(premiumAmountSchema),
    defaultValues: {
      employeeMonthlyContribution:
        selectedPlan?.employeeMonthlyContribution ?? undefined,
      premium: selectedPlan?.premium,
    },
  });

  const premium = premiumAmountForm.watch('premium');
  const allowance =
    onboardingPeriod?.allowance ??
    selectedPlan?.employerMonthlyContribution ??
    0;
  const employerMonthlyContribution = Math.min(allowance, premium);

  useEffect(() => {
    if (!premium) {
      return;
    }
    const employeeMonthlyContribution = Math.max(
      new Decimal(premium).sub(employerMonthlyContribution).toNumber(),
      0
    );

    if (
      employeeMonthlyContribution ===
      premiumAmountForm.getValues().employeeMonthlyContribution
    ) {
      return;
    }

    premiumAmountForm.setValue(
      'employeeMonthlyContribution',
      employeeMonthlyContribution
    );
  }, [employerMonthlyContribution, premium, premiumAmountForm]);

  return {
    premiumAmountForm,
    employerMonthlyContribution,
    onSubmit: async () => {
      if (!onboardingPeriodId || !selectedBenefit) return false;

      const data = premiumAmountForm.getValues();

      try {
        await mutateBenefitPremium(
          [
            selectedBenefit.id,
            idempotencyKey.get(),
            {
              premium: data.premium,
            },
          ],
          { flowId }
        );

        return true;
      } catch {
        /* empty */
      }
      return false;
    },
  };
};
